import { useEffect, useMemo, useState } from "react";
import { AdminLayout } from "../../components/AdminLayout";
import { AdminFilterBar } from "../../components/admin/AdminFilterBar";
import { adminHeaders, api } from "../../lib/api";
import { prettyRawJson, rewardStatusLabel, rewardTypeLabel, voucherTypeLabel } from "../../lib/adminDisplayLabels";
import { isLocalDevFrontend } from "../../lib/env";
import { money, shortDate } from "../../lib/format";

type Severity = "normal" | "info" | "warning" | "critical";

interface AdminTag {
  tag: string;
  severity: Severity;
  message: string;
  recommended_owner: "customer_service" | "operations" | "engineering";
}

interface VoucherRow {
  id: number;
  member_id: number;
  member_account: string;
  voucher_type: string;
  status: string;
  source_activity_code: string;
  source_record_id: string | null;
  expires_at: string | null;
  created_at: string;
  used_at: string | null;
}

interface VoucherTransactionRow {
  id: number;
  voucher_id: number | null;
  member_account: string;
  voucher_type: string;
  action: string;
  activity_code: string;
  unique_key: string;
  note: string | null;
  created_at: string;
}

interface ReservationRow {
  id: number;
  campaign_code: string;
  voucher_id: number;
  member_account: string;
  reserve_period_type?: string | null;
  reserve_period_key?: string | null;
  reserved_amount: number;
  actual_amount: number | null;
  released_amount: number;
  status: string;
  reserved_at: string;
  consumed_at: string | null;
  released_at: string | null;
  release_reason: string | null;
}

interface DrawRow {
  id: number;
  draw_ref: string;
  member_account: string;
  voucher_id: number;
  campaign_code: string;
  voucher_type: string;
  reward_type: string;
  selected_item_code: string | null;
  selected_amount: number | null;
  reward_id: number | null;
  task_id: number | null;
  status: string;
  fail_reason: string | null;
  error_stage: string | null;
  last_error: string | null;
  review_required: number;
  review_status: string | null;
  created_at: string;
  updated_at: string;
}

interface RewardRow {
  id: number;
  member_account: string;
  reward_type: string;
  reward_status: string;
  amount: number;
  created_at: string;
  claimed_at: string | null;
  unique_key: string | null;
  source_deposit_id: number | null;
}

interface TaskRow {
  id: number;
  reward_id: number;
  member_account: string;
  reward_type: string;
  amount: number;
  dispatch_mode: string;
  dispatch_status: string;
  dispatch_method: string;
  scheduled_at: string | null;
  queued_at: string | null;
  completed_at: string | null;
  failed_at: string | null;
  failed_reason: string | null;
  attempt_count: number;
}

interface NewbieProgressRow {
  member_account: string;
  step6_status: string | null;
  step6_voucher_id: number | null;
  step6_draw_id: string | null;
  step6_draw_record_id: number | null;
  step6_reward_id: number | null;
  step6_completed_at: string | null;
}

interface MemberRow {
  id: number;
  account: string;
  member_account?: string | null;
  status: string;
  is_test?: number | null;
  is_abnormal?: number | null;
}

interface V1DrawCheck {
  found: boolean;
  rows: Record<string, unknown>[];
}

interface AuditLogRow {
  id: number;
  action: string;
  actor_type: string;
  actor_id: string | null;
  campaign_code: string | null;
  voucher_id: number | null;
  draw_ref: string | null;
  reward_id: number | null;
  task_id: number | null;
  reason: string | null;
  created_at: string;
  before_json: string | null;
  after_json: string | null;
}

interface VoucherSummary {
  status_tags: AdminTag[];
  severity: Severity;
  member: MemberRow | null;
  voucher: VoucherRow | null;
  voucher_transactions: VoucherTransactionRow[];
  reservation: ReservationRow | null;
  draw: DrawRow | null;
  reward: RewardRow | null;
  task: TaskRow | null;
  newbie_progress: NewbieProgressRow | null;
  v1_draw_check: V1DrawCheck;
  audit_timeline: AuditLogRow[];
  inferred_event_status: string;
  inferred_confidence: string;
  suggested_action: string;
  coverage_warnings: string[];
}

interface VoucherListItem {
  voucher: VoucherRow;
  reservation: ReservationRow | null;
  draw: DrawRow | null;
  reward: RewardRow | null;
  task: TaskRow | null;
  inferred_event_status: string;
  inferred_confidence: string;
  severity: Severity;
  status_tags: AdminTag[];
  suggested_action: string;
}

interface Pagination {
  limit: number;
  offset: number;
  count: number;
  has_more: boolean;
  next_offset: number | null;
}

interface VoucherListResponse {
  items: VoucherListItem[];
  pagination: Pagination;
}

interface VoucherDetailResponse {
  voucher_summary: VoucherSummary;
}

interface TimelineEvent {
  event_type: string;
  occurred_at: string;
  voucher_id?: number | null;
  draw_ref?: string | null;
  reward_id?: number | null;
  task_id?: number | null;
  action?: string | null;
  summary: string;
}

interface MemberTimeline {
  member: MemberRow | null;
  newbie_progress: NewbieProgressRow | null;
  vouchers: VoucherSummary[];
  timeline_events: TimelineEvent[];
  coverage_warnings: string[];
  pagination: Pagination;
}

interface MemberTimelineResponse {
  member_timeline: MemberTimeline;
}

interface AdminIssueResponse {
  member: {
    id: number;
    account: string;
    member_account: string;
    is_test: number | null;
    status: string | null;
  };
  campaign: {
    id: number;
    campaign_code: string;
    voucher_type: string;
    reward_type: string;
    estimated_cost_per_voucher: number | null;
  };
  voucher: VoucherRow;
  reservation: ReservationRow;
  issue: {
    source_activity_code: string;
    source_record_id: string;
    reserve_period_type: string;
    reserve_period_key: string;
    reserved_amount: number;
    expected_reward_amounts: number[];
    note: string | null;
  };
}

interface AdminIssueLogEntry {
  id: number;
  action: string;
  result: "success" | "failed";
  actor: string | null;
  account: string | null;
  campaign_code: string | null;
  voucher_type: string | null;
  reward_type: string | null;
  voucher_id: number | null;
  reservation_id: number | null;
  reserved_amount: number | null;
  reserve_period_type: string | null;
  reserve_period_key: string | null;
  source: string | null;
  note: string | null;
  failure_reason: string | null;
  error_code: string | null;
  request_id: string | null;
  created_at: string;
}

interface AdminIssueLogResponse {
  issue_logs: AdminIssueLogEntry[];
}

const voucherTypeOptions = [
  "",
  "newbie_launch_red_packet",
  "newbie_bonus_red_packet",
  "graduate_launch_red_packet",
  "advanced_launch_red_packet",
  "weekly_bright_red_packet",
  "weekend_bright_red_packet",
  "weekend_advanced_red_packet"
];

const voucherStatusOptions = ["", "available", "used", "expired", "cancelled", "voided"];

const sourceActivityOptions = [
  "",
  "v2_newbie_step6_red_packet",
  "v2_weekly_bright_red_packet",
  "v2_weekend_bright_red_packet",
  "v2_graduate_launch_red_packet",
  "newbie_step5_completed",
  "newbie_step6_red_packet",
  "red-packet-v2"
];

const adminIssueCampaignOptions = [
  { code: "weekly_bright", label: "週耀紅包", voucherType: "weekly_bright_red_packet" },
  { code: "weekend_bright", label: "週末紅包", voucherType: "weekend_bright_red_packet" },
  { code: "graduate_launch", label: "畢業紅包", voucherType: "graduate_launch_red_packet" }
];

const voucherStatusLabels: Record<string, string> = {
  available: "可使用",
  used: "已使用",
  expired: "已過期",
  cancelled: "已取消",
  voided: "已作廢"
};

const reservationStatusLabels: Record<string, string> = {
  reserved: "已保留預算",
  consumed: "已扣除預算",
  released: "已釋放",
  failed: "保留失敗"
};

const drawStatusLabels: Record<string, string> = {
  task_enqueued: "已建立派發任務",
  reward_created: "獎勵已建立",
  pending_review: "等待人工確認",
  needs_review: "異常需工程處理",
  quota_exhausted: "預算已滿",
  no_available_items: "無可用獎項",
  reservation_missing: "預算保留缺失",
  already_drawn: "已開啟",
  already_opened: "已開啟",
  failed_retryable: "可重試錯誤",
  drawing: "抽獎處理中"
};

const taskStatusLabels: Record<string, string> = {
  pending: "任務待處理",
  processing: "派發中",
  completed: "派發完成",
  failed: "派發失敗"
};

const inferredStatusLabels: Record<string, string> = {
  draw_linked: "已連到抽獎記錄",
  reservation_missing: "預算保留缺失",
  no_available_items: "無可用獎項",
  possible_v2_disabled_or_not_allowlisted: "活動未開啟或不在名單",
  v2_identity_present: "新版紅包券防呆",
  possible_v2_identity_present: "疑似新版紅包券防呆",
  voucher_used_without_draw: "券已使用但無抽獎記錄",
  unknown: "未知狀態"
};

const tagLabels: Record<string, string> = {
  normal_v2_success: "V2 流程正常",
  waiting_dispatch: "等待派發",
  task_enqueue_skipped: "已略過自動派發任務",
  task_enqueued: "已建立派發任務",
  pending_review: "等待人工確認",
  reservation_missing: "預算保留缺失",
  no_available_items: "無可用獎項",
  possible_v2_disabled_or_not_allowlisted: "活動未開啟或不在名單",
  v2_identity_present: "新版身份券防線",
  needs_review: "異常需工程處理",
  task_enqueue_failed: "派發任務建立失敗",
  quota_exhausted: "預算已滿",
  voucher_used_without_draw: "券已用但無抽獎記錄",
  reservation_consumed_without_usage: "預算已扣除但缺使用量",
  v2_written_to_v1_draw_table: "疑似寫入舊版抽獎表",
  step6_progress_polluted: "第 6 關進度疑似被 V2 污染"
};

const severityLabels: Record<Severity, string> = {
  normal: "正常",
  info: "提醒",
  warning: "需留意",
  critical: "緊急處理"
};

const ownerLabels: Record<AdminTag["recommended_owner"], string> = {
  customer_service: "客服",
  operations: "營運",
  engineering: "工程"
};

const eventTypeLabels: Record<string, string> = {
  voucher: "紅包券",
  voucher_transaction: "券異動",
  reservation: "預算保留",
  draw: "抽獎記錄",
  reward: "獎勵",
  task: "派發任務",
  audit: "稽核紀錄"
};

const coverageWarningLabels: Record<string, string> = {
  no_draw_blocked_event_not_visible_in_draw_centric_mvp1a: "此狀態屬於未建立抽獎紀錄的阻擋事件，需用會員時間線查詢，非抽獎紀錄頁可完整呈現。",
  no_draw_event_has_no_audit_log: "此阻擋事件沒有對應稽核紀錄，請以紅包券狀態與會員時間線為主，必要時交由工程確認。"
};

const generalStatusLabels: Record<string, string> = {
  active: "正常",
  newbie_active: "新人進行中",
  unlocked: "已解鎖",
  locked: "未解鎖",
  completed: "已完成",
  true: "是",
  false: "否",
  "1": "是",
  "0": "否",
  null: "無",
  undefined: "無"
};

const forbiddenActionsText = "只讀頁面：不可重抽、不可改券狀態、不可直接建立獎勵、抽獎或派發任務、不可改走舊版流程。";

export function AdminRedPacketV2Page() {
  const isLocalDev = isLocalDevFrontend();
  const params = useMemo(() => new URLSearchParams(window.location.search), []);
  const [memberAccount, setMemberAccount] = useState(params.get("member_account") ?? "");
  const [voucherType, setVoucherType] = useState(params.get("voucher_type") ?? "newbie_launch_red_packet");
  const [status, setStatus] = useState(params.get("status") ?? "");
  const [sourceActivityCode, setSourceActivityCode] = useState(params.get("source_activity_code") ?? "");
  const [limit, setLimit] = useState(params.get("limit") ?? "20");
  const [offset, setOffset] = useState(params.get("offset") ?? "0");
  const [list, setList] = useState<VoucherListResponse | null>(null);
  const [detail, setDetail] = useState<VoucherSummary | null>(null);
  const [timeline, setTimeline] = useState<MemberTimeline | null>(null);
  const [selectedVoucherId, setSelectedVoucherId] = useState<number | null>(null);
  const [loadingList, setLoadingList] = useState(false);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [loadingTimeline, setLoadingTimeline] = useState(false);
  const [error, setError] = useState("");
  const [detailError, setDetailError] = useState("");
  const [timelineError, setTimelineError] = useState("");
  const [issueAccount, setIssueAccount] = useState("");
  const [issueCampaignCode, setIssueCampaignCode] = useState(adminIssueCampaignOptions[0].code);
  const [issueNote, setIssueNote] = useState("");
  const [issueLoading, setIssueLoading] = useState(false);
  const [issueResult, setIssueResult] = useState<AdminIssueResponse | null>(null);
  const [issueError, setIssueError] = useState("");
  const [issueLogs, setIssueLogs] = useState<AdminIssueLogEntry[]>([]);
  const [issueLogsLoading, setIssueLogsLoading] = useState(false);

  useEffect(() => {
    if (isLocalDev) void loadIssueLogs();
  }, [isLocalDev]);

  async function loadIssueLogs() {
    setIssueLogsLoading(true);
    try {
      const result = await api<AdminIssueLogResponse>("/api/admin/red-packet-v2/vouchers/issue?limit=20", { headers: adminHeaders() });
      setIssueLogs(result.issue_logs ?? []);
    } catch {
      setIssueLogs([]);
    } finally {
      setIssueLogsLoading(false);
    }
  }

  async function search() {
    setError("");
    setDetailError("");
    setTimelineError("");
    setDetail(null);
    setSelectedVoucherId(null);
    setLoadingList(true);
    try {
      const query = buildQuery({
        member_account: memberAccount,
        voucher_type: voucherType,
        status,
        source_activity_code: sourceActivityCode,
        limit: normalizeLimit(limit),
        offset: normalizeOffset(offset)
      });
      const nextList = await api<VoucherListResponse>(`/api/admin/red-packet-v2/vouchers${query}`, { headers: adminHeaders() });
      setList(nextList);
      if (memberAccount.trim()) {
        await loadTimeline(memberAccount.trim());
      } else {
        setTimeline(null);
      }
    } catch (err) {
      setList(null);
      setTimeline(null);
      setError(safeAdminError(err, "讀取 v2 紅包券列表失敗。"));
    } finally {
      setLoadingList(false);
    }
  }

  async function loadDetail(voucherId: number) {
    setSelectedVoucherId(voucherId);
    setDetail(null);
    setDetailError("");
    setLoadingDetail(true);
    try {
      const result = await api<VoucherDetailResponse>(`/api/admin/red-packet-v2/vouchers/${voucherId}`, { headers: adminHeaders() });
      setDetail(result.voucher_summary);
    } catch (err) {
      setDetailError(safeAdminError(err, "讀取紅包券明細失敗。"));
    } finally {
      setLoadingDetail(false);
    }
  }

  async function loadTimeline(account: string) {
    const trimmed = account.trim();
    if (!trimmed) {
      setTimeline(null);
      setTimelineError("請先輸入會員帳號。");
      return;
    }
    setTimelineError("");
    setLoadingTimeline(true);
    try {
      const query = buildQuery({
        member_account: trimmed,
        voucher_type: voucherType,
        status,
        source_activity_code: sourceActivityCode,
        limit: normalizeLimit(limit),
        offset: normalizeOffset(offset)
      });
      const result = await api<MemberTimelineResponse>(`/api/admin/red-packet-v2/member-timeline${query}`, { headers: adminHeaders() });
      setTimeline(result.member_timeline);
    } catch (err) {
      setTimeline(null);
      setTimelineError(safeAdminError(err, "讀取會員時間線失敗。"));
    } finally {
      setLoadingTimeline(false);
    }
  }

  async function issueAdminVoucher() {
    const account = issueAccount.trim();
    if (!account) {
      setIssueError("請先輸入會員帳號。");
      return;
    }
    const selected = adminIssueCampaignOptions.find((option) => option.code === issueCampaignCode) ?? adminIssueCampaignOptions[0];
    const confirmed = window.confirm(
      `確認手動發放 ${selected.label}？\n\n本操作只會建立紅包券與保留額度，不會抽獎、不會建立 reward、不會建立派發任務，也不會派點。`
    );
    if (!confirmed) return;
    setIssueError("");
    setIssueResult(null);
    setIssueLoading(true);
    try {
      const result = await api<AdminIssueResponse>("/api/admin/red-packet-v2/vouchers/issue", {
        method: "POST",
        headers: adminHeaders(),
        body: JSON.stringify({
          account,
          campaign_code: issueCampaignCode,
          note: issueNote.trim() || null
        })
      });
      setIssueResult(result);
      setMemberAccount(account);
      setVoucherType(result.voucher.voucher_type);
      setStatus("available");
      setSourceActivityCode(result.issue.source_activity_code);
      await loadTimeline(account);
      await loadIssueLogs();
    } catch (err) {
      setIssueError(safeAdminError(err, "Admin 發券失敗"));
      await loadIssueLogs();
    } finally {
      setIssueLoading(false);
    }
  }

  function reset() {
    setMemberAccount("");
    setVoucherType("newbie_launch_red_packet");
    setStatus("");
    setSourceActivityCode("");
    setLimit("20");
    setOffset("0");
    setList(null);
    setDetail(null);
    setTimeline(null);
    setSelectedVoucherId(null);
    setError("");
    setDetailError("");
    setTimelineError("");
    setIssueResult(null);
    setIssueError("");
  }

  return (
    <AdminLayout
      title="紅包 V2 管理台"
      subtitle="查詢紅包券狀態、檢查派發進度，並在 local 驗收中手動發放週耀、週末、畢業紅包券。"
      actions={<button className="admin-btn admin-btn-primary" onClick={search} disabled={loadingList}>{loadingList ? "查詢中..." : "查詢"}</button>}
    >
      <section className="admin-card">
        <div className="admin-card-head">
          <span>查詢區</span>
          <h2>紅包券狀態查詢</h2>
        </div>
        <div className="admin-rpv2-readonly-banner">
          <strong>只讀查詢</strong>
          <span>可查會員、券、預算保留、抽獎、reward 與派發任務狀態。本區不會改資料。</span>
        </div>
        <AdminFilterBar>
          <label>
            會員帳號
            <input
              value={memberAccount}
              onChange={(event) => setMemberAccount(event.target.value)}
              placeholder={isLocalDev ? "test_step6_v2_m5b_reward_created_20260712_001" : "輸入會員帳號"}
            />
          </label>
          <label>
            紅包券類型
            <select value={voucherType} onChange={(event) => setVoucherType(event.target.value)}>
              {voucherTypeOptions.map((value) => <option key={value || "all"} value={value}>{value ? voucherTypeLabel(value) : "全部券種"}</option>)}
            </select>
          </label>
          <label>
            紅包券狀態
            <select value={status} onChange={(event) => setStatus(event.target.value)}>
              {voucherStatusOptions.map((value) => <option key={value || "all"} value={value}>{value ? voucherStatusLabel(value) : "全部狀態"}</option>)}
            </select>
          </label>
          <label>
            來源活動
            <select value={sourceActivityCode} onChange={(event) => setSourceActivityCode(event.target.value)}>
              {sourceActivityOptions.map((value) => <option key={value || "all"} value={value}>{value ? sourceActivityLabel(value) : "全部來源"}</option>)}
            </select>
          </label>
          <label>
            每頁筆數
            <input type="number" min="1" max="100" value={limit} onChange={(event) => setLimit(event.target.value)} />
          </label>
          <label>
            起始筆數
            <input type="number" min="0" value={offset} onChange={(event) => setOffset(event.target.value)} />
          </label>
          <button className="admin-btn admin-btn-primary" type="button" onClick={search} disabled={loadingList}>{loadingList ? "查詢中..." : "查詢"}</button>
          <button className="admin-btn admin-btn-secondary" type="button" onClick={reset}>重設</button>
        </AdminFilterBar>
        {error ? <p className="error-box">{error}</p> : null}
      </section>

      {isLocalDev ? (
        <AdminIssueVoucherPanel
          isLocalDev={isLocalDev}
          account={issueAccount}
          campaignCode={issueCampaignCode}
          note={issueNote}
          loading={issueLoading}
          result={issueResult}
          error={issueError}
          onAccountChange={setIssueAccount}
          onCampaignChange={setIssueCampaignCode}
          onNoteChange={setIssueNote}
          onSubmit={issueAdminVoucher}
        />
      ) : null}

      <AdminRedPacketOperationsGuide
        issueLogs={issueLogs}
        issueLogsLoading={issueLogsLoading}
        onRefreshLogs={loadIssueLogs}
      />

      <VoucherListSection
        list={list}
        loading={loadingList}
        selectedVoucherId={selectedVoucherId}
        onSelectVoucher={loadDetail}
      />

      <VoucherDetailSection
        detail={detail}
        loading={loadingDetail}
        error={detailError}
        selectedVoucherId={selectedVoucherId}
      />

      <MemberTimelineSection
        timeline={timeline}
        loading={loadingTimeline}
        error={timelineError}
        memberAccount={memberAccount}
        onLoad={() => loadTimeline(memberAccount)}
      />
    </AdminLayout>
  );
}

function AdminIssueVoucherPanel({
  isLocalDev,
  account,
  campaignCode,
  note,
  loading,
  result,
  error,
  onAccountChange,
  onCampaignChange,
  onNoteChange,
  onSubmit
}: {
  isLocalDev: boolean;
  account: string;
  campaignCode: string;
  note: string;
  loading: boolean;
  result: AdminIssueResponse | null;
  error: string;
  onAccountChange: (value: string) => void;
  onCampaignChange: (value: string) => void;
  onNoteChange: (value: string) => void;
  onSubmit: () => void;
}) {
  const selected = adminIssueCampaignOptions.find((option) => option.code === campaignCode) ?? adminIssueCampaignOptions[0];
  const featureStatus = result ? "可發券" : error.includes("feature_disabled") ? "未開啟" : "待確認";
  const previewHref = result && isLocalDev ? `/red-packet?previewAccount=${encodeURIComponent(result.member.member_account)}` : "";

  return (
    <section className="admin-card admin-rpv2-issue-card">
      <div className="admin-card-head">
        <span>手動發券區</span>
        <h2>手動發放紅包券</h2>
      </div>
      <p className="admin-rpv2-issue-subtitle">
        只建立紅包券與保留額度，不會抽獎、不會派點。玩家需到紅包中心自行抽取。
      </p>
      <div className="admin-rpv2-issue-status-grid">
        <article>
          <span>發券狀態</span>
          <strong>{featureStatus}</strong>
          <p>僅限受控 local 驗收。</p>
        </article>
        <article>
          <span>發券結果</span>
          <strong>建券 + 保留額度</strong>
          <p>不會建立 reward 或派發任務。</p>
        </article>
        <article>
          <span>玩家下一步</span>
          <strong>自行到紅包中心抽取</strong>
          <p>前端點格不決定金額。</p>
        </article>
      </div>
      <div className="admin-rpv2-issue-campaigns" aria-label="目前允許發券活動">
        {adminIssueCampaignOptions.map((option) => (
          <span className={option.code === campaignCode ? "is-active" : ""} key={option.code}>
            {option.label}
          </span>
        ))}
      </div>
      <div className="admin-rpv2-issue-form">
        <label>
          活動選擇
          <select value={campaignCode} onChange={(event) => onCampaignChange(event.target.value)}>
            {adminIssueCampaignOptions.map((option) => (
              <option key={option.code} value={option.code}>
                {option.label}（{option.code}）
              </option>
            ))}
          </select>
        </label>
        <label>
          會員帳號
          <input
            value={account}
            onChange={(event) => onAccountChange(event.target.value)}
            placeholder="test_admin_issue_4h_weekly_001"
          />
        </label>
        <label>
          備註
          <input
            value={note}
            onChange={(event) => onNoteChange(event.target.value)}
            placeholder="Phase 4H local smoke only"
          />
        </label>
        <button className="admin-btn admin-btn-primary" type="button" onClick={onSubmit} disabled={loading}>
          {loading ? "發券中..." : "發放紅包券"}
        </button>
      </div>
      <p className="admin-rpv2-issue-footnote">
        目前選擇：{selected.label}。發券成功後只代表 voucher + reservation 已建立。
      </p>
      {error ? <p className="error-box">{error}</p> : null}
      {result ? (
        <div className="admin-rpv2-issue-result">
          <div className="admin-rpv2-issue-result-head">
            <div>
              <span>發券成功</span>
              <strong>{result.member.member_account}</strong>
            </div>
            {previewHref ? (
              <a className="admin-btn admin-btn-secondary" href={previewHref} target="_blank" rel="noreferrer">
                查看此前台紅包中心
              </a>
            ) : null}
          </div>
          <div className="admin-rpv2-issue-result-grid">
            <span>會員帳號：{result.member.member_account}</span>
            <span>活動名稱：{campaignLabel(result.campaign.campaign_code)}</span>
            <span>voucher id：#{result.voucher.id}</span>
            <span>reservation id：#{result.reservation.id}</span>
            <span>reserved amount：{money(result.issue.reserved_amount)}</span>
            <span>period key：{result.issue.reserve_period_key}</span>
          </div>
          <p>下一步：請玩家到紅包中心抽紅包。本次尚未 draw、尚未建立 reward、尚未建立 dispatch task。</p>
          {previewHref ? <p>Local preview：{previewHref}</p> : null}
        </div>
      ) : null}
    </section>
  );
}

function AdminRedPacketOperationsGuide({
  issueLogs,
  issueLogsLoading,
  onRefreshLogs
}: {
  issueLogs: AdminIssueLogEntry[];
  issueLogsLoading: boolean;
  onRefreshLogs: () => void;
}) {
  return (
    <section className="admin-card admin-rpv2-ops-guide">
      <div className="admin-card-head">
        <span>說明區</span>
        <h2>營運操作邊界</h2>
      </div>
      <div className="admin-rpv2-ops-grid">
        <article>
          <strong>可發放活動</strong>
          <span>週耀紅包、週末紅包、畢業紅包。</span>
        </article>
        <article>
          <strong>不可操作</strong>
          <span>新人第 6 關、VIP、高級紅包與未知活動不在本面板開放。</span>
        </article>
        <article>
          <strong>安全原則</strong>
          <span>前端不指定金額或中獎格，抽中金額只由後端 V2 pool 決定。</span>
        </article>
        <article>
          <strong>完成判斷</strong>
          <span>發券後需玩家到紅包中心抽取，再等待 reward 與派發閉環。</span>
        </article>
      </div>
      <div className="admin-rpv2-issue-log-head">
        <div>
          <strong>最近 Admin 發券紀錄</strong>
          <p>只讀顯示最近 20 筆成功 / 失敗紀錄，不含 token 或帳密。</p>
        </div>
        <button className="admin-btn admin-btn-secondary" type="button" onClick={onRefreshLogs} disabled={issueLogsLoading}>
          {issueLogsLoading ? "讀取中..." : "刷新紀錄"}
        </button>
      </div>
      <AdminIssueLogTable rows={issueLogs} loading={issueLogsLoading} />
    </section>
  );
}

function AdminIssueLogTable({ rows, loading }: { rows: AdminIssueLogEntry[]; loading: boolean }) {
  if (loading) return <p className="admin-muted">正在讀取最近發券紀錄...</p>;
  if (!rows.length) return <p className="admin-muted">目前沒有 Admin 發券紀錄。</p>;

  return (
    <div className="admin-rpv2-table-wrap admin-rpv2-issue-log-table">
      <table>
        <thead>
          <tr>
            <th>時間</th>
            <th>結果</th>
            <th>操作者</th>
            <th>會員</th>
            <th>活動</th>
            <th>券種</th>
            <th>voucher</th>
            <th>reservation</th>
            <th>預留</th>
            <th>period</th>
            <th>原因 / 備註</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.id}>
              <td>{shortDate(row.created_at)}</td>
              <td><span className={row.result === "success" ? "status-pill success" : "status-pill danger"}>{row.result === "success" ? "成功" : "失敗"}</span></td>
              <td>{displayValue(row.actor)}</td>
              <td><LongText value={row.account} /></td>
              <td>{campaignLabel(row.campaign_code)}</td>
              <td>{row.voucher_type ? voucherTypeLabel(row.voucher_type) : "-"}</td>
              <td>{row.voucher_id ? `#${row.voucher_id}` : "-"}</td>
              <td>{row.reservation_id ? `#${row.reservation_id}` : "-"}</td>
              <td>{row.reserved_amount ? money(row.reserved_amount) : "-"}</td>
              <td>{row.reserve_period_type && row.reserve_period_key ? `${row.reserve_period_type} / ${row.reserve_period_key}` : "-"}</td>
              <td><LongText value={row.result === "failed" ? row.error_code || row.failure_reason : row.note} /></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function VoucherListSection({
  list,
  loading,
  selectedVoucherId,
  onSelectVoucher
}: {
  list: VoucherListResponse | null;
  loading: boolean;
  selectedVoucherId: number | null;
  onSelectVoucher: (voucherId: number) => void;
}) {
  return (
    <section className="admin-card">
      <div className="admin-card-head">
        <span>紅包券</span>
        <h2>紅包券列表</h2>
      </div>
      {loading ? <p className="admin-muted">讀取 v2 紅包券中...</p> : null}
      {!loading && !list ? <p className="admin-muted">請輸入條件後查詢 v2 紅包券。</p> : null}
      {!loading && list && !list.items.length ? <p className="empty">查無 v2 紅包券。</p> : null}
      {list?.items.length ? (
        <>
          <div className="table-wrap admin-rpv2-table-wrap">
            <table>
              <thead>
                <tr>
                  <th>券號</th>
                  <th>會員帳號</th>
                  <th>券類型</th>
                  <th>來源活動</th>
                  <th>券狀態</th>
                  <th>預算</th>
                  <th>抽獎</th>
                  <th>獎勵</th>
                  <th>派發任務</th>
                  <th>事件判斷</th>
                  <th>風險等級</th>
                  <th>建立時間</th>
                  <th>使用時間</th>
                  <th>到期日</th>
                  <th>詳情</th>
                </tr>
              </thead>
              <tbody>
                {list.items.map((item) => (
                  <tr key={item.voucher.id}>
                    <td>#{item.voucher.id}</td>
                    <td><LongText value={item.voucher.member_account} /></td>
                    <td><CodeLabel label={voucherTypeLabel(item.voucher.voucher_type)} code={item.voucher.voucher_type} /></td>
                    <td><CodeLabel label={sourceActivityLabel(item.voucher.source_activity_code)} code={item.voucher.source_activity_code} /></td>
                    <td><StatusPill value={item.voucher.status} domain="voucher" /></td>
                    <td><StatusPill value={item.reservation?.status ?? "-"} domain="reservation" /></td>
                    <td><StatusPill value={item.draw?.status ?? "-"} domain="draw" /></td>
                    <td><StatusPill value={item.reward?.reward_status ?? "-"} domain="reward" /></td>
                    <td><StatusPill value={item.task?.dispatch_status ?? "-"} domain="task" /></td>
                    <td><CodeLabel label={inferredStatusLabel(item.inferred_event_status)} code={item.inferred_event_status} /></td>
                    <td><SeverityPill severity={item.severity} /></td>
                    <td>{shortDate(item.voucher.created_at)}</td>
                    <td>{shortDate(item.voucher.used_at)}</td>
                    <td>{shortDate(item.voucher.expires_at)}</td>
                    <td>
                      <button
                        className="admin-btn admin-btn-secondary"
                        type="button"
                        onClick={() => onSelectVoucher(item.voucher.id)}
                        disabled={selectedVoucherId === item.voucher.id}
                      >
                        {selectedVoucherId === item.voucher.id ? "已選取" : "查看詳情"}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <PaginationView pagination={list.pagination} />
        </>
      ) : null}
    </section>
  );
}

function VoucherDetailSection({
  detail,
  loading,
  error,
  selectedVoucherId
}: {
  detail: VoucherSummary | null;
  loading: boolean;
  error: string;
  selectedVoucherId: number | null;
}) {
  const summary = detail ? buildOperationalSummary(detail) : null;
  const customerConclusion = detail ? buildCustomerConclusion(detail) : null;
  const drawResult = detail ? buildDrawResult(detail) : null;
  const riskJudgement = detail ? buildRiskJudgement(detail) : null;

  return (
    <section className="admin-card">
      <div className="admin-card-head">
        <span>紅包券明細</span>
        <h2>紅包券明細</h2>
      </div>
      {!selectedVoucherId ? <p className="admin-muted">請從紅包券列表選取一筆資料查看完整關聯。</p> : null}
      {loading ? <p className="admin-muted">讀取紅包券 #{selectedVoucherId} 明細中...</p> : null}
      {error ? <p className="error-box">{error}</p> : null}
      {detail && summary && customerConclusion && drawResult && riskJudgement ? (
        <>
          <section className="admin-rpv2-section admin-rpv2-conclusion">
            <h3>客服處理結論</h3>
          </section>
          <div className="admin-rpv2-summary-grid">
            <SummaryCard title="客服處理結論" value={customerConclusion.value} helper={customerConclusion.helper} tone={customerConclusion.tone} />
            <SummaryCard title="抽獎結果" value={drawResult.value} helper={drawResult.helper} tone={drawResult.tone} />
            <SummaryCard title="風險判斷" value={riskJudgement.value} helper={riskJudgement.helper} tone={riskJudgement.tone} />
          </div>

          <TagList tags={detail.status_tags} />
          <WarningList warnings={detail.coverage_warnings} />

          <SectionGrid title="營運必要資料" rows={[
            ["會員帳號", detail.voucher?.member_account],
            ["券狀態", detail.voucher ? labelWithCode(voucherStatusLabel(detail.voucher.status), detail.voucher.status) : "-"],
            ["抽獎金額", drawResult.amount ?? "-"],
            ["Reward 狀態", detail.reward ? rewardStatusLabel(detail.reward.reward_status) : "尚未建立 reward"],
            ["Dispatch 狀態", detail.task ? taskStatusLabel(detail.task.dispatch_status) : "尚未建立 dispatch task"],
            ["Reservation 狀態", detail.reservation ? reservationStatusLabel(detail.reservation.status) : "尚未建立 reservation"],
            ["Legacy draw", detail.v1_draw_check?.found ? "有，需工程確認" : "0"]
          ]} />

          <details className="admin-rpv2-technical-details">
            <summary>展開技術資料</summary>
            <SectionGrid title="紅包券技術欄位" rows={[
              ["voucher id", detail.voucher ? `#${detail.voucher.id}` : "-"],
              ["券類型", detail.voucher ? labelWithCode(voucherTypeLabel(detail.voucher.voucher_type), detail.voucher.voucher_type) : "-"],
              ["來源活動", detail.voucher ? labelWithCode(sourceActivityLabel(detail.voucher.source_activity_code), detail.voucher.source_activity_code) : "-"],
              ["source_record_id", technicalDisplayLabel(detail.voucher?.source_record_id)],
              ["建立時間", shortDate(detail.voucher?.created_at)],
              ["使用時間", shortDate(detail.voucher?.used_at)],
              ["到期日", shortDate(detail.voucher?.expires_at)]
            ]} />

            <SectionGrid title="Reservation 技術欄位" rows={detail.reservation ? [
              ["reservation id", `#${detail.reservation.id}`],
              ["活動", campaignLabel(detail.reservation.campaign_code)],
              ["period type", detail.reservation.reserve_period_type ?? "-"],
              ["period key", detail.reservation.reserve_period_key ?? "-"],
              ["保留金額", detail.reservation.reserved_amount],
              ["實際金額", detail.reservation.actual_amount ?? "-"],
              ["釋放金額", detail.reservation.released_amount],
              ["保留時間", shortDate(detail.reservation.reserved_at)],
              ["結算時間", shortDate(detail.reservation.consumed_at)],
              ["釋放原因", detail.reservation.release_reason]
            ] : [["reservation", "無預算保留資料"]]} />

            <SectionGrid title="Draw / Reward / Dispatch 技術欄位" rows={[
              ["draw id", detail.draw ? `#${detail.draw.id}` : "-"],
              ["draw ref", technicalDisplayLabel(detail.draw?.draw_ref)],
              ["draw status", detail.draw ? labelWithCode(drawStatusLabel(detail.draw.status), detail.draw.status) : "-"],
              ["selected item", technicalDisplayLabel(detail.draw?.selected_item_code)],
              ["reward id", detail.reward ? `#${detail.reward.id}` : detail.draw?.reward_id ? `#${detail.draw.reward_id}` : "-"],
              ["reward type", detail.reward ? rewardTypeLabel(detail.reward.reward_type) : "-"],
              ["reward amount", detail.reward?.amount ?? "-"],
              ["dispatch task id", detail.task ? `#${detail.task.id}` : detail.draw?.task_id ? `#${detail.draw.task_id}` : "-"],
              ["dispatch mode", dispatchModeLabel(detail.task?.dispatch_mode)],
              ["dispatch failed reason", failureReasonLabel(detail.task?.failed_reason)],
              ["review required", detail.draw?.review_required ? "是" : "否"],
              ["review status", detail.draw?.review_status ? labelWithCode(drawStatusLabel(detail.draw.review_status), detail.draw.review_status) : "-"]
            ]} />

            <SectionGrid title="新人第 6 關 / 舊版流程檢查" rows={[
              ["第 6 關狀態", progressStatusLabel(detail.newbie_progress?.step6_status)],
              ["step6_voucher_id", detail.newbie_progress?.step6_voucher_id ?? "-"],
              ["legacy step6_draw_id", detail.newbie_progress?.step6_draw_id ?? "-"],
              ["v2 step6_draw_record_id", detail.newbie_progress?.step6_draw_record_id ?? "-"],
              ["step6_reward_id", detail.newbie_progress?.step6_reward_id ?? "-"],
              ["第 6 關完成時間", shortDate(detail.newbie_progress?.step6_completed_at)],
              ["是否寫入舊版抽獎表", detail.v1_draw_check?.found ? "是，需工程確認" : "否"]
            ]} />

            <TransactionTable rows={detail.voucher_transactions} />
            <AuditTimeline rows={detail.audit_timeline} />
            <RawDetails title="Raw audit / engineering fields" data={detail} />
          </details>
        </>
      ) : null}
    </section>
  );
}

function MemberTimelineSection({
  timeline,
  loading,
  error,
  memberAccount,
  onLoad
}: {
  timeline: MemberTimeline | null;
  loading: boolean;
  error: string;
  memberAccount: string;
  onLoad: () => void;
}) {
  return (
    <section className="admin-card">
      <div className="admin-card-head">
        <span>會員紅包時間線</span>
        <h2>會員紅包時間線</h2>
      </div>
      <p className="admin-muted">依會員帳號串起紅包券、券異動、預算保留、抽獎、獎勵、派發任務與未建立抽獎紀錄的阻擋狀態推論。</p>
      <button className="admin-btn admin-btn-secondary" type="button" onClick={onLoad} disabled={loading || !memberAccount.trim()}>
        {loading ? "讀取中..." : "重新讀取會員時間線"}
      </button>
      {error ? <p className="error-box">{error}</p> : null}
      {!timeline && !loading ? <p className="admin-muted">輸入會員帳號並查詢後，這裡會顯示會員紅包時間線。</p> : null}
      {timeline ? (
        <>
          <SectionGrid title="會員 / 第 6 關進度" rows={[
            ["會員帳號", timeline.member?.account ?? timeline.member?.member_account ?? memberAccount],
            ["會員狀態", memberStatusLabel(timeline.member?.status)],
            ["測試帳號", timeline.member?.is_test ? "是" : "否"],
            ["異常帳號", timeline.member?.is_abnormal ? "是" : "否"],
            ["第 6 關狀態", progressStatusLabel(timeline.newbie_progress?.step6_status)],
            ["第 6 關紅包券 ID", timeline.newbie_progress?.step6_voucher_id ?? "-"],
            ["第 6 關舊抽獎 ID", timeline.newbie_progress?.step6_draw_id ?? "-"],
            ["第 6 關 V2 抽獎紀錄 ID", timeline.newbie_progress?.step6_draw_record_id ?? "-"],
            ["第 6 關獎勵 ID", timeline.newbie_progress?.step6_reward_id ?? "-"],
            ["第 6 關完成時間", shortDate(timeline.newbie_progress?.step6_completed_at)]
          ]} />
          <WarningList warnings={timeline.coverage_warnings} />
          <TimelineEvents rows={timeline.timeline_events} />
          <TimelineVoucherTable rows={timeline.vouchers} />
          <PaginationView pagination={timeline.pagination} />
          <RawDetails title="技術資料，工程排查用" data={timeline} />
        </>
      ) : null}
    </section>
  );
}

function TimelineVoucherTable({ rows }: { rows: VoucherSummary[] }) {
  if (!rows.length) return <p className="empty">目前沒有 v2 紅包券時間線資料。</p>;
  return (
    <div className="table-wrap admin-rpv2-table-wrap">
      <table>
        <thead>
          <tr>
            <th>券號</th>
            <th>券狀態</th>
            <th>預算</th>
            <th>抽獎</th>
            <th>獎勵</th>
            <th>派發任務</th>
            <th>事件判斷</th>
            <th>風險等級</th>
            <th>建議處理</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.voucher?.id ?? `${row.inferred_event_status}-${row.voucher?.member_account ?? "unknown"}`}>
              <td>{row.voucher ? `#${row.voucher.id}` : "-"}</td>
              <td><StatusPill value={row.voucher?.status ?? "-"} domain="voucher" /></td>
              <td><StatusPill value={row.reservation?.status ?? "-"} domain="reservation" /></td>
              <td><StatusPill value={row.draw?.status ?? "-"} domain="draw" /></td>
              <td><StatusPill value={row.reward?.reward_status ?? "-"} domain="reward" /></td>
              <td><StatusPill value={row.task?.dispatch_status ?? "-"} domain="task" /></td>
              <td><CodeLabel label={inferredStatusLabel(row.inferred_event_status)} code={row.inferred_event_status} /></td>
              <td><SeverityPill severity={row.severity} /></td>
              <td><LongText value={row.suggested_action || "-"} /></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function TimelineEvents({ rows }: { rows: TimelineEvent[] }) {
  if (!rows.length) return <p className="empty">目前沒有時間線事件。</p>;
  return (
    <section className="admin-rpv2-section">
      <h3>時間線事件</h3>
      <div className="admin-rpv2-timeline">
        {rows.map((row, index) => (
          <article className="admin-rpv2-event" key={`${row.occurred_at}-${row.event_type}-${index}`}>
            <div>
              <span className="admin-rpv2-event-time">{shortDate(row.occurred_at)}</span>
              <strong>{eventTypeLabel(row.event_type)}</strong>
            </div>
            <p>{eventSummaryLabel(row.summary)}</p>
            <div className="admin-rpv2-event-meta">
              <span>券：{row.voucher_id ? `#${row.voucher_id}` : "-"}</span>
              <span>抽獎：<TechnicalText value={row.draw_ref} /></span>
              <span>獎勵：{row.reward_id ? `#${row.reward_id}` : "-"}</span>
              <span>任務：{row.task_id ? `#${row.task_id}` : "-"}</span>
            </div>
          </article>
        ))}
      </div>
    </section>
  );
}

function TransactionTable({ rows }: { rows: VoucherTransactionRow[] }) {
  if (!rows.length) return <p className="empty">目前沒有券異動紀錄。</p>;
  return (
    <section className="admin-rpv2-section">
      <h3>券異動紀錄</h3>
      <div className="table-wrap admin-rpv2-table-wrap">
        <table>
          <thead>
            <tr>
              <th>ID</th>
              <th>動作</th>
              <th>活動來源</th>
              <th>識別資料</th>
              <th>備註</th>
              <th>建立時間</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.id}>
                <td>#{row.id}</td>
                <td>{transactionActionLabel(row.action)}</td>
                <td><CodeLabel label={sourceActivityLabel(row.activity_code)} code={row.activity_code} /></td>
                <td><TechnicalText value={row.unique_key} /></td>
                <td><LongText value={noteLabel(row.note)} /></td>
                <td>{shortDate(row.created_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function AuditTimeline({ rows }: { rows: AuditLogRow[] }) {
  if (!rows.length) return <p className="empty">目前沒有稽核紀錄。</p>;
  return (
    <section className="admin-rpv2-section">
      <h3>稽核紀錄</h3>
      <div className="table-wrap admin-rpv2-table-wrap">
        <table>
          <thead>
            <tr>
              <th>時間</th>
              <th>動作</th>
              <th>操作者</th>
              <th>券</th>
              <th>抽獎</th>
              <th>獎勵</th>
              <th>任務</th>
              <th>原因</th>
              <th>原始差異</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.id}>
                <td>{shortDate(row.created_at)}</td>
                <td><CodeLabel label={auditActionLabel(row.action)} code={row.action} /></td>
                <td>{actorLabel(row.actor_type, row.actor_id)}</td>
                <td>{row.voucher_id ? `#${row.voucher_id}` : "-"}</td>
                <td><TechnicalText value={row.draw_ref} /></td>
                <td>{row.reward_id ? `#${row.reward_id}` : "-"}</td>
                <td>{row.task_id ? `#${row.task_id}` : "-"}</td>
                <td><LongText value={reasonLabel(row.reason)} /></td>
                <td>
                  <details className="admin-rpv2-inline-raw">
                    <summary>查看</summary>
                    <pre>{prettyRawJson({ before_json: row.before_json, after_json: row.after_json })}</pre>
                  </details>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function SectionGrid({ title, rows }: { title: string; rows: Array<[string, string | number | null | undefined]> }) {
  return (
    <section className="admin-rpv2-section">
      <h3>{title}</h3>
      <div className="admin-rpv2-detail-grid">
        {rows.map(([label, value]) => (
          <div className="admin-rpv2-kv" key={label}>
            <span>{label}</span>
            <strong><LongText value={displayValue(value)} /></strong>
          </div>
        ))}
      </div>
    </section>
  );
}

function SummaryCard({ title, value, helper, tone }: { title: string; value: string; helper?: string; tone?: Severity }) {
  return (
    <article className={`admin-rpv2-summary-card ${tone ? `tone-${tone}` : ""}`}>
      <span>{title}</span>
      <strong>{value}</strong>
      {helper ? <p>{helper}</p> : null}
    </article>
  );
}

function TagList({ tags }: { tags: AdminTag[] }) {
  if (!tags.length) return <p className="admin-muted">目前沒有狀態標籤。</p>;
  return (
    <section className="admin-rpv2-section">
      <h3>狀態標籤</h3>
      <div className="admin-rpv2-tag-list">
        {tags.map((row) => (
          <article className="admin-rpv2-tag" key={`${row.tag}-${row.severity}`}>
            <SeverityPill severity={row.severity} />
            <div>
              <strong title={row.tag}>{tagLabel(row.tag)}</strong>
              <p>{tagMessageLabel(row.message, row.tag)}｜建議負責：{ownerLabels[row.recommended_owner] ?? row.recommended_owner}</p>
            </div>
          </article>
        ))}
      </div>
    </section>
  );
}

function WarningList({ warnings }: { warnings: string[] }) {
  if (!warnings.length) return null;
  return (
    <div className="error-box">
      <strong>覆蓋提醒</strong>
      <ul>
        {warnings.map((warning) => <li key={warning} title={warning}>{coverageWarningLabel(warning)}</li>)}
      </ul>
    </div>
  );
}

function RawDetails({ title, data }: { title: string; data: unknown }) {
  return (
    <details className="raw-details admin-rpv2-raw">
      <summary>{title}</summary>
      <pre>{prettyRawJson(data)}</pre>
    </details>
  );
}

function PaginationView({ pagination }: { pagination: Pagination }) {
  return (
    <p className="admin-muted">
      每頁 {pagination.limit} 筆｜跳過 {pagination.offset} 筆｜本次 {pagination.count} 筆｜{pagination.has_more ? `還有下一頁：${pagination.next_offset}` : "沒有更多資料"}
    </p>
  );
}

function StatusPill({ value, domain }: { value: string | null | undefined; domain: "voucher" | "reservation" | "draw" | "reward" | "task" }) {
  const text = value || "-";
  const label = statusLabel(domain, text);
  return <span className={statusClass(text)} title={text}>{label}</span>;
}

function SeverityPill({ severity }: { severity: Severity }) {
  return <span className={severityClass(severity)} title={severity}>{severityLabel(severity)}</span>;
}

function CodeLabel({ label, code }: { label: string; code: string | null | undefined }) {
  return (
    <span className="admin-rpv2-code" title={code || label}>
      {label}
    </span>
  );
}

function LongText({ value }: { value: string | number | null | undefined }) {
  return <span className="admin-rpv2-long" title={displayValue(value)}>{displayValue(value)}</span>;
}

function TechnicalText({ value }: { value: string | number | null | undefined }) {
  return <span className="admin-rpv2-long" title={displayValue(value)}>{technicalDisplayLabel(value)}</span>;
}

function buildCustomerConclusion(detail: VoucherSummary): { value: string; helper: string; tone: Severity } {
  const hasDraw = Boolean(detail.draw);
  const rewardStatus = detail.reward?.reward_status ?? "";
  const taskStatus = detail.task?.dispatch_status ?? "";

  if (detail.severity === "critical") {
    return {
      value: "需工程處理，客服先不要操作",
      helper: "資料狀態有風險，只能查詢與回報。",
      tone: "critical"
    };
  }
  if (hasDraw && rewardStatus === "completed" && taskStatus === "completed") {
    return {
      value: "V2 紅包已完成，獎勵派發已完成",
      helper: "可回覆玩家紅包與派發皆已完成。",
      tone: "normal"
    };
  }
  if (hasDraw && detail.reward && detail.task) {
    return {
      value: "V2 紅包已完成，等待派發完成",
      helper: "已建立 reward 與 dispatch task，後續看派發狀態。",
      tone: "info"
    };
  }
  if (hasDraw && detail.reward) {
    return {
      value: "V2 紅包已完成，獎勵已建立",
      helper: "尚未看到派發任務完成，請依 SOP 查詢。",
      tone: "info"
    };
  }
  return {
    value: "尚未完成 V2 紅包流程",
    helper: translateOperationalText(detail.suggested_action) || "請依狀態標籤查詢，不要手動補資料。",
    tone: detail.severity
  };
}

function buildDrawResult(detail: VoucherSummary): { value: string; helper: string; tone: Severity; amount: string | number | null } {
  const amount = detail.draw?.selected_amount ?? detail.reward?.amount ?? null;
  if (amount != null) {
    return {
      value: `抽中 ${money(amount)} 點`,
      helper: detail.draw?.selected_item_code ? `獎項：${detail.draw.selected_item_code}` : "已取得抽獎金額。",
      tone: "normal",
      amount
    };
  }
  return {
    value: "尚未看到抽獎金額",
    helper: detail.draw ? "抽獎紀錄存在，但沒有金額欄位。" : "尚未建立 V2 抽獎紀錄。",
    tone: detail.draw ? "warning" : "info",
    amount: null
  };
}

function buildRiskJudgement(detail: VoucherSummary): { value: string; helper: string; tone: Severity } {
  if (detail.v1_draw_check?.found) {
    return {
      value: "需處理，偵測到舊版流程",
      helper: "legacy draw 不為 0，請工程確認，不要人工修資料。",
      tone: "critical"
    };
  }
  if (detail.severity === "normal") {
    return {
      value: "正常，未走舊版流程",
      helper: "legacy draw = 0，V2 關聯資料可追蹤。",
      tone: "normal"
    };
  }
  if (detail.severity === "critical") {
    return {
      value: "需處理，資料狀態有風險",
      helper: "客服只能查詢並回報，交由工程確認。",
      tone: "critical"
    };
  }
  return {
    value: "需留意，請依標籤判斷",
    helper: `目前風險等級：${severityLabel(detail.severity)}。`,
    tone: detail.severity
  };
}

function buildOperationalSummary(detail: VoucherSummary) {
  const tagSet = new Set(detail.status_tags.map((tag) => tag.tag));
  if (detail.severity === "critical") {
    return {
      title: "緊急處理，需工程確認",
      description: "資料出現不一致或可能污染，客服只能查詢並升級，不可改資料。",
      owner: "建議負責：工程",
      nextAction: "停止人工操作，請工程依券號、抽獎編號與稽核紀錄追查。"
    };
  }
  if (tagSet.has("pending_review") || detail.draw?.status === "pending_review") {
    return {
      title: "等待人工確認",
      description: "紅包已抽出需審核獎項，尚未建立獎勵或派發任務。",
      owner: "建議負責：營運 / 客服",
      nextAction: "依審核流程確認，不可自動派發或讓玩家重抽。"
    };
  }
  if (tagSet.has("waiting_dispatch") || detail.draw?.status === "reward_created") {
    return {
      title: "獎勵已建立，等待派發",
      description: tagSet.has("task_enqueue_skipped")
        ? "V2 抽獎已完成並建立獎勵；目前沒有派發任務，需依設定確認派發流程。"
        : "V2 抽獎已完成並建立獎勵，等待後續派發處理。",
      owner: "建議負責：客服 / 營運",
      nextAction: "查看獎勵狀態與派發任務；不可直接改獎勵狀態。"
    };
  }
  if (detail.draw?.status === "task_enqueued" || tagSet.has("task_enqueued")) {
    return {
      title: "派發任務已建立",
      description: "V2 抽獎已完成，獎勵與派發任務已進入處理流程。",
      owner: "建議負責：客服",
      nextAction: "等待派發結果，延遲時再升級營運或工程。"
    };
  }
  if (detail.inferred_event_status === "reservation_missing") {
    return {
      title: "預算保留缺失",
      description: "券存在但缺少必要預算保留紀錄，不能改走舊版流程，也不能手動補抽獎紀錄。",
      owner: "建議負責：工程",
      nextAction: "升級工程檢查紅包券、預算保留與稽核紀錄。"
    };
  }
  if (detail.inferred_event_status === "no_available_items") {
    return {
      title: "目前無可用獎項",
      description: "活動或獎項設定使紅包 fail closed，券不應被消耗。",
      owner: "建議負責：營運 / 工程",
      nextAction: "檢查活動獎項與預算，不可讓玩家重抽或改券狀態。"
    };
  }
  if (detail.inferred_event_status === "v2_identity_present" || tagSet.has("v2_identity_present")) {
    return {
      title: "系統偵測已有新版紅包券",
      description: "已阻止舊版流程繼續，券沒有被消耗，尚未建立抽獎紀錄、獎勵或派發任務。",
      owner: "建議負責：工程 / 營運",
      nextAction: "請升級工程 / 營運確認新版紅包券處理狀態。不可改走舊版流程，不可重抽，不可改券狀態，不可直接建立獎勵、抽獎或派發任務。"
    };
  }
  return {
    title: inferredStatusLabel(detail.inferred_event_status),
    description: "依查詢狀態與關聯資料判斷，請搭配下方狀態標籤與稽核紀錄查核。",
    owner: `建議負責：${detail.status_tags[0]?.recommended_owner ? ownerLabels[detail.status_tags[0].recommended_owner] : "客服"}`,
    nextAction: translateOperationalText(detail.suggested_action) || "依 V2 抽獎紀錄與稽核紀錄查詢。"
  };
}

function statusClass(value: string) {
  if (value === "used" || value === "completed" || value === "consumed" || value === "task_enqueued") return "status-pill success";
  if (value === "available" || value === "ready" || value === "reserved" || value === "reward_created") return "status-pill soon";
  if (value === "pending" || value === "processing" || value === "pending_review") return "status-pill warn";
  if (value === "needs_review" || value === "failed" || value === "voucher_used_without_draw") return "status-pill danger";
  if (value === "-") return "status-pill muted";
  return "status-pill live";
}

function severityClass(severity: Severity) {
  if (severity === "critical") return "status-pill danger";
  if (severity === "warning") return "status-pill warn";
  if (severity === "info") return "status-pill live";
  return "status-pill good";
}

function buildQuery(values: Record<string, string | number | null | undefined>) {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(values)) {
    if (value === null || value === undefined) continue;
    const text = String(value).trim();
    if (!text) continue;
    params.set(key, text);
  }
  const query = params.toString();
  return query ? `?${query}` : "";
}

function normalizeLimit(value: string) {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return 20;
  return Math.min(Math.max(Math.trunc(parsed), 1), 100);
}

function normalizeOffset(value: string) {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return 0;
  return Math.max(Math.trunc(parsed), 0);
}

function displayValue(value: string | number | null | undefined) {
  if (value === null || value === undefined || value === "") return "-";
  const text = String(value);
  return generalStatusLabels[text] ?? text;
}

function labelWithCode(label: string, code: string | null | undefined) {
  return label || code || "-";
}

function statusLabel(domain: "voucher" | "reservation" | "draw" | "reward" | "task", value: string) {
  if (!value || value === "-") {
    if (domain === "reservation") return "無預算保留";
    if (domain === "draw") return "尚未抽獎";
    if (domain === "reward") return "無獎勵";
    if (domain === "task") return "無派發任務";
    return "-";
  }
  if (domain === "voucher") return voucherStatusLabel(value);
  if (domain === "reservation") return reservationStatusLabel(value);
  if (domain === "draw") return drawStatusLabel(value);
  if (domain === "reward") return rewardStatusLabel(value);
  return taskStatusLabel(value);
}

function voucherStatusLabel(value?: string | null) {
  if (!value) return "-";
  return voucherStatusLabels[value] ?? value;
}

function reservationStatusLabel(value?: string | null) {
  if (!value) return "-";
  return reservationStatusLabels[value] ?? value;
}

function drawStatusLabel(value?: string | null) {
  if (!value) return "-";
  return drawStatusLabels[value] ?? value;
}

function taskStatusLabel(value?: string | null) {
  if (!value) return "-";
  return taskStatusLabels[value] ?? value;
}

function inferredStatusLabel(value?: string | null) {
  if (!value) return "-";
  return inferredStatusLabels[value] ?? value;
}

function tagLabel(value?: string | null) {
  if (!value) return "-";
  return tagLabels[value] ?? value;
}

function severityLabel(value: Severity) {
  return severityLabels[value] ?? value;
}

function sourceActivityLabel(value?: string | null) {
  if (!value) return "-";
  if (value === "v2_newbie_step6_red_packet") return "新人第 6 關 V2 發券";
  if (value === "v2_weekly_bright_red_packet") return "週耀紅包 V2 發券";
  if (value === "v2_weekend_bright_red_packet") return "週末紅包 V2 發券";
  if (value === "v2_graduate_launch_red_packet") return "畢業紅包 V2 發券";
  if (value === "newbie_step5_completed") return "新人第 5 關完成";
  if (value === "newbie_step6_red_packet") return "新人第 6 關紅包";
  if (value === "red-packet-v2") return "紅包 V2 抽獎";
  if (value === "newbie_step6_v2_reservation_created") return "建立第 6 關 V2 預算保留";
  if (value === "draw_started") return "抽獎開始";
  if (value === "draw_completed_snapshot") return "抽獎快照完成";
  if (value === "reward_created") return "獎勵已建立";
  if (value === "reward_pending_review") return "等待人工確認";
  if (value === "task_enqueue_skipped") return "略過自動派發任務";
  if (value === "voucher_used") return "紅包券已使用";
  if (value === "budget_guard_blocked") return "預算 / 獎項限制阻擋";
  return value;
}

function transactionActionLabel(value?: string | null) {
  if (value === "issue") return "發券";
  if (value === "use") return "使用";
  if (value === "release") return "釋放";
  return value || "-";
}

function eventTypeLabel(value?: string | null) {
  if (!value) return "-";
  return eventTypeLabels[value] ?? value;
}

function eventSummaryLabel(value?: string | null) {
  if (!value) return "-";
  const [first, second, third] = value.split(":");
  if (first === "newbie_launch_red_packet" && second === "available" && third === "newbie_step5_completed") {
    return "新人第 5 關完成，紅包券可使用";
  }
  if (first === "newbie_launch_red_packet" && second === "available" && third === "v2_newbie_step6_red_packet") {
    return "新人第 6 關 V2 紅包券可使用";
  }
  if (first === "issue" && second) {
    return `${sourceActivityLabel(second)}發券`;
  }
  return translateOperationalText(value);
}

function auditActionLabel(value?: string | null) {
  if (!value) return "-";
  if (value === "newbie_step6_v2_reservation_created") return "建立第 6 關 V2 預算保留";
  if (value === "draw_started") return "抽獎開始";
  if (value === "draw_completed_snapshot") return "抽獎快照完成";
  if (value === "reward_created") return "獎勵已建立";
  if (value === "reward_pending_review") return "等待人工確認";
  if (value === "task_enqueue_skipped") return "略過自動派發任務";
  if (value === "voucher_used") return "紅包券已使用";
  if (value === "budget_guard_blocked") return "預算 / 獎項限制阻擋";
  return value;
}

function coverageWarningLabel(value?: string | null) {
  if (!value) return "-";
  return coverageWarningLabels[value] ?? translateOperationalText(value);
}

function translateOperationalText(value?: string | null) {
  if (!value) return "";
  return value
    .replaceAll("fallback V1", "改走舊版流程")
    .replaceAll("cannot fallback V1", "不可改走舊版流程")
    .replaceAll("不可 fallback V1", "不可改走舊版流程")
    .replaceAll("reward_created", "獎勵已建立")
    .replaceAll("task_enqueued", "已建立派發任務")
    .replaceAll("pending_review", "等待人工確認")
    .replaceAll("needs_review", "異常需工程處理")
    .replaceAll("no_available_items", "無可用獎項")
    .replaceAll("v2_identity_present", "新版紅包券防呆")
    .replaceAll("reservation_missing", "缺少預算保留")
    .replaceAll("quota_exhausted", "預算已滿")
    .replaceAll("already_drawn", "已開啟")
    .replaceAll("already_opened", "已開啟")
    .replaceAll("budget_guard_blocked", "預算 / 獎項限制阻擋")
    .replaceAll("all item quota unavailable", "所有獎項額度皆不可用")
    .replaceAll("total_item_quantity_limit_reached", "獎項總數量已達上限")
    .replaceAll("newbie_step5_completed", "新人第 5 關完成")
    .replaceAll("v2_newbie_step6_red_packet", "新人第 6 關 V2 發券")
    .replaceAll("red-packet-v2", "紅包 V2 抽獎")
    .replaceAll("draw record", "抽獎紀錄")
    .replaceAll("audit timeline", "稽核紀錄")
    .replaceAll("voucher", "紅包券")
    .replaceAll("reservation", "預算保留")
    .replaceAll("reward", "獎勵")
    .replaceAll("draw", "抽獎")
    .replaceAll("task", "派發任務");
}

function technicalDisplayLabel(value?: string | number | null) {
  if (value === null || value === undefined || value === "") return "-";
  return "已記錄（工程資料）";
}

function memberStatusLabel(value?: string | null) {
  if (!value) return "-";
  return generalStatusLabels[value] ?? value;
}

function progressStatusLabel(value?: string | null) {
  if (!value) return "-";
  return generalStatusLabels[value] ?? drawStatusLabel(value);
}

function campaignLabel(value?: string | null) {
  if (!value) return "-";
  if (value === "newbie_step6_launch") return "新人第 6 關紅包活動";
  if (value === "weekly_bright") return "週耀紅包";
  if (value === "weekend_bright") return "週末紅包";
  if (value === "graduate_launch") return "畢業紅包";
  if (value === "advanced_launch") return "高級啟航紅包";
  if (value === "weekend_advanced") return "高級週末紅包";
  return value;
}

function dispatchModeLabel(value?: string | null) {
  if (!value) return "-";
  if (value === "crawler") return "派發排程";
  if (value === "manual") return "人工派發";
  if (value === "disabled") return "未啟用派發任務";
  return value;
}

function failureReasonLabel(value?: string | null) {
  if (!value) return "-";
  return translateOperationalText(value);
}

function noteLabel(value?: string | null) {
  if (!value) return "-";
  if (value.includes("smoke_fixture")) return "測試情境資料";
  return translateOperationalText(value);
}

function reasonLabel(value?: string | null) {
  if (!value) return "-";
  return translateOperationalText(value);
}

function tagMessageLabel(message: string | null | undefined, tag: string) {
  const translated = translateOperationalText(message);
  if (!translated || translated === message && /[_:]/.test(translated)) return tagLabel(tag);
  return translated;
}

function actorLabel(actorType?: string | null, actorId?: string | null) {
  const type = actorType === "system" ? "系統" : actorType === "admin" ? "管理員" : actorType || "-";
  return actorId ? `${type}（${actorId}）` : type;
}

function safeAdminError(err: unknown, fallback: string) {
  const message = err instanceof Error ? err.message : "";
  if (message === "Unauthorized") {
    return [
      "目前尚未取得管理員權限，查詢與發券功能無法使用。",
      isLocalDevFrontend() ? "Local 測試請在 Console 設定 admin_token，例如：localStorage.setItem(\"admin_token\", \"<local-admin-token>\")" : "請確認已使用有效的管理員權限。"
    ].join(" ");
  }
  if (message === "invalid_voucher_id") return "紅包券 ID 格式不正確，請輸入正整數。";
  if (message.includes("not found")) return "查無這筆 V2 紅包券資料。";
  return fallback;
}
